/**
 * PageHeader — canonical page title block (GUI makeover item 18).
 *
 * Every route header renders through this: h1 `.page-title` (Space Grotesk
 * display face), optional
 * leading icon, one-line subtitle, and a right-aligned actions slot that
 * wraps on mobile. Page roots pair this with
 * `<div className="space-y-6 animate-fade-in">`.
 */
import { clsx } from 'clsx'
import { ArrowLeft } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

interface Props {
  title: string
  icon?: LucideIcon
  /** Tailwind text-* class for the icon (defaults to text-blue-light). */
  iconClassName?: string
  subtitle?: ReactNode
  /** Right-aligned actions (buttons, selects). Falsy values render nothing. */
  actions?: ReactNode
  /** Sub-page back link (ArrowLeft + label) rendered above the title row. */
  backTo?: { to: string; label: string }
  /** Extra classes on the header row (e.g. mb-* on pages without space-y roots). */
  className?: string
}

export default function PageHeader({ title, icon: Icon, iconClassName, subtitle, actions, backTo, className }: Props) {
  const row = (
    <div className={clsx('flex items-center justify-between flex-wrap gap-3', !backTo && className)}>
      <div>
        <h1 className="page-title flex items-center gap-2">
          {Icon && <Icon size={20} className={iconClassName ?? 'text-blue-light'} aria-hidden />}
          {title}
        </h1>
        {subtitle && <p className="text-sm text-text-secondary mt-0.5">{subtitle}</p>}
      </div>
      {actions ? <div className="flex items-center gap-2 flex-wrap">{actions}</div> : null}
    </div>
  )
  if (!backTo) return row
  // Sub-page idiom: back link above the title row; className moves to the
  // wrapper so mb-* spacing still applies to the whole header block.
  return (
    <div className={className}>
      <Link
        to={backTo.to}
        className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary mb-2"
      >
        <ArrowLeft size={16} aria-hidden /> {backTo.label}
      </Link>
      {row}
    </div>
  )
}
